import { useEffect, useState } from "react";
import { api, euro } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TrendingUp, Target, Users, Building2, Megaphone } from "lucide-react";

const SOURCE_LABEL = {
  tram: "Tranvía / metro",
  flyers: "Flyers",
  rrss: "Redes sociales",
  boca_a_boca: "Boca a boca",
  calle: "Calle",
  web: "Web",
  otro: "Otro",
  "—": "Sin especificar",
};

export default function ConversionReport() {
  const { user, centers } = useAuth();
  const [report, setReport] = useState(null);
  const [filters, setFilters] = useState({
    date_from: "",
    date_to: "",
    center_id: "all",
  });

  const load = async () => {
    const params = {};
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    if (["admin", "center_admin"].includes(user?.role) && filters.center_id !== "all") params.center_id = filters.center_id;
    const { data } = await api.get("/reports/conversion", { params });
    setReport(data);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [filters]);

  if (!report) return <div className="text-[#6E7772]">Cargando…</div>;

  const s = report.summary;

  return (
    <div className="space-y-6" data-testid="conversion-report-page">
      <div>
        <div className="text-xs tracking-[0.2em] uppercase text-[#6E7772] font-semibold">
          Comercial
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-[#2A2F2C] mt-1">
          Informe de conversión
        </h1>
        <p className="text-sm text-[#6E7772] mt-1">
          % de presupuestos aceptados sobre emitidos, por trabajador, centro y procedencia.
        </p>
      </div>

      {/* Filters */}
      <Card className="border-[#E5E3DB] shadow-none bg-white">
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label>Desde</Label>
            <Input
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
              data-testid="filter-from"
            />
          </div>
          <div>
            <Label>Hasta</Label>
            <Input
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
              data-testid="filter-to"
            />
          </div>
          {["admin", "center_admin"].includes(user?.role) && (
            <div>
              <Label>Centro</Label>
              <Select
                value={filters.center_id}
                onValueChange={(v) => setFilters({ ...filters, center_id: v })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {centers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex items-end">
            <Button
              variant="outline"
              onClick={() => setFilters({ date_from: "", date_to: "", center_id: "all" })}
            >
              Limpiar filtros
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Emitidos" value={s.emitidos} icon={Target} tone="bg-white" />
        <KpiCard label="Aceptados" value={s.aceptados} icon={TrendingUp} tone="bg-[#D1E0D7]" />
        <KpiCard label="Tasa conversión" value={`${s.conversion_rate}%`} icon={Target} tone="bg-[#EAE6DF]" highlight />
        <KpiCard label="Importe aceptado" value={euro(s.total_aceptado)} icon={TrendingUp} tone="bg-[#F3E5D8]" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ReportTable
          title="Por trabajador"
          icon={Users}
          rows={report.by_employee}
          testid="report-by-employee"
        />
        <ReportTable
          title="Por centro"
          icon={Building2}
          rows={report.by_center}
          testid="report-by-center"
        />
        <ReportTable
          title="Por procedencia"
          icon={Megaphone}
          rows={report.by_source.map((r) => ({ ...r, label: SOURCE_LABEL[r.key] || r.label || "—" }))}
          testid="report-by-source"
        />
      </div>
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, tone, highlight }) {
  return (
    <Card className={`border-[#E5E3DB] shadow-none ${tone}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs tracking-wider uppercase font-semibold text-[#6E7772]">
              {label}
            </div>
            <div className={`font-heading mt-2 tracking-tight ${highlight ? "text-3xl text-[#4A5D4E]" : "text-2xl text-[#2A2F2C]"}`}>
              {value}
            </div>
          </div>
          <Icon className="h-5 w-5 text-[#4A5D4E]" strokeWidth={1.5} />
        </div>
      </CardContent>
    </Card>
  );
}

function ReportTable({ title, icon: Icon, rows, testid }) {
  return (
    <Card className="border-[#E5E3DB] shadow-none bg-white" data-testid={testid}>
      <CardHeader>
        <CardTitle className="font-heading font-medium text-base flex items-center gap-2">
          <Icon className="h-4 w-4 text-[#4A5D4E]" strokeWidth={1.5} /> {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <div className="text-sm text-[#6E7772] text-center py-6">Sin datos.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-[#E5E3DB]">
                <TableHead></TableHead>
                <TableHead className="text-right">Emit.</TableHead>
                <TableHead className="text-right">Acep.</TableHead>
                <TableHead className="text-right">%</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.key} className="border-[#E5E3DB]">
                  <TableCell className="font-medium">{r.label}</TableCell>
                  <TableCell className="text-right">{r.emitidos}</TableCell>
                  <TableCell className="text-right">{r.aceptados}</TableCell>
                  <TableCell className="text-right">
                    <Badge
                      className={
                        r.conversion_rate >= 50
                          ? "bg-[#D1E0D7] text-[#2A2F2C]"
                          : r.conversion_rate >= 25
                          ? "bg-[#F3E5D8] text-[#2A2F2C]"
                          : "bg-[#E8D1D1] text-[#2A2F2C]"
                      }
                    >
                      {r.conversion_rate}%
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
